import type { ApprovalState, PolicyClass } from "./policy.js";
import { SharedWorkflowStateValue } from "./sharedValues.js";
import type { PublicToolCatalogEntry } from "./tooling.js";

export type LocaleCode = "en" | "es" | "ru";

export enum IncidentStatus {
  Suspected = "suspected",
  Investigating = "investigating",
  Identified = "identified",
  Mitigating = "mitigating",
  Monitoring = "monitoring",
  Resolved = "resolved",
  Closed = "closed",
  Suppressed = "suppressed"
}

export enum IncidentSeverity {
  Sev0 = "sev0",
  Sev1 = "sev1",
  Sev2 = "sev2",
  Sev3 = "sev3",
  Sev4 = "sev4"
}

export enum IncidentRunbookType {
  PublicApiOutage = "public_api_outage",
  DenialOfServiceAttack = "denial_of_service_attack",
  WorkerSchedulerStall = "worker_scheduler_stall",
  GitHubDeliveryFailure = "github_delivery_failure",
  NotificationProviderOutage = "notification_provider_outage",
  DataBreach = "data_breach",
  MiddlewareExceptionStorm = "middleware_exception_storm",
  ProviderWebhookVerificationFailure = "provider_webhook_verification_failure",
  TelephonyRoutingOutage = "telephony_routing_outage",
  TelephonyProviderOutage = "telephony_provider_outage",
  FreeSwitchServiceFailure = "freeswitch_service_failure",
  NetworkEdgeMisconfiguration = "network_edge_misconfiguration",
  FloatingIpMisassignment = "floating_ip_misassignment",
  VpsHostDegradation = "vps_host_degradation",
  EdgeProxyFailure = "edge_proxy_failure",
  CaddyHostingFailure = "caddy_hosting_failure",
  DatabaseDegradation = "database_degradation",
  PostgresPrimaryRoleConflict = "postgres_primary_role_conflict",
  StorageWriteContention = "storage_write_contention",
  SecretCompromise = "secret_compromise",
  LlmProviderDegradation = "llm_provider_degradation",
  ControlPlanePartialOutage = "control_plane_partial_outage"
}

export enum IncidentSignalSource {
  EndpointCheck = "endpoint_check",
  SyntheticCheck = "synthetic_check",
  ProviderWebhook = "provider_webhook",
  OperatorChat = "operator_chat",
  OperatorVoice = "operator_voice",
  ManualApi = "manual_api",
  WorkerEvent = "worker_event"
}

export enum IncidentSignalConfidence {
  Unverified = "unverified",
  Reported = "reported",
  Corroborated = "corroborated",
  Confirmed = "confirmed"
}

export type IncidentFeedAuthority = "bootstrap_backfill_only";
export type IncidentFeedPurpose = "local_bootstrap" | "backfill";

export interface IncidentFeedMetadata {
  readonly mode: "incident-feed";
  readonly authority: IncidentFeedAuthority;
  readonly labelKey: string;
  readonly purposes: readonly IncidentFeedPurpose[];
  readonly productionEventAuthority: false;
}

export enum IncidentAffectedAudience {
  Internal = "internal",
  Customers = "customers",
  Unknown = "unknown"
}

export enum IncidentNotificationState {
  NotRequired = "not_required",
  ApprovalRequired = SharedWorkflowStateValue.ApprovalRequired,
  Queued = "queued",
  Sending = "sending",
  Sent = "sent",
  PartiallyFailed = "partially_failed",
  Failed = "failed",
  Paused = "paused"
}

export enum IncidentCommunicationTransition {
  PostOutageNotice = "post_outage_notice",
  SendCustomerNotification = "send_customer_notification",
  MarkDeliveryFailed = "mark_delivery_failed",
  HoldDelivery = "hold_delivery",
  SendResolutionNotification = "send_resolution_notification"
}

export enum IncidentApprovalTransition {
  RequestApproval = "request_approval",
  Approve = "approve",
  Reject = "reject",
  Reassign = "reassign",
  OwnerAcknowledge = "owner_acknowledge",
  EscalateOwner = "escalate_owner"
}

export enum IncidentDecisionKind {
  NotificationApproval = "notification_approval",
  RecoveryApproval = "recovery_approval",
  ElevatedAccess = "elevated_access",
  OwnerEscalation = "owner_escalation",
  EvidenceGap = "evidence_gap",
  VerificationFailure = "verification_failure"
}

export enum IncidentDecisionState {
  Open = "open",
  Acknowledged = "acknowledged",
  Approved = SharedWorkflowStateValue.Approved,
  Denied = "denied",
  Expired = "expired",
  Resolved = "resolved"
}

export enum IncidentOwnerEscalationState {
  Queued = "queued",
  Sent = "sent",
  Acknowledged = "acknowledged",
  Failed = "failed",
  Suppressed = "suppressed"
}

export enum IncidentResponderKind {
  Human = "human",
  Ai = "ai",
  System = "system",
  Vendor = "vendor"
}

export enum IncidentResponderRole {
  IncidentCommander = "incident_commander",
  DiagnosticResponder = "diagnostic_responder",
  RecoveryResponder = "recovery_responder",
  CommunicationsResponder = "communications_responder",
  CustomerImpactResponder = "customer_impact_responder",
  ExternalEscalationResponder = "external_escalation_responder",
  CodeBridgeResponder = "code_bridge_responder",
  Approver = "approver",
  ServiceOwner = "service_owner",
  PostIncidentLearningResponder = "post_incident_learning_responder"
}

export enum IncidentResponderAcknowledgementState {
  NotRequired = "not_required",
  Pending = "pending",
  Acknowledged = "acknowledged",
  Declined = "declined",
  Escalated = "escalated"
}

export enum IncidentTaskStatus {
  Proposed = "proposed",
  Assigned = "assigned",
  Acknowledged = "acknowledged",
  InProgress = "in_progress",
  WaitingApproval = "waiting_approval",
  Blocked = SharedWorkflowStateValue.Blocked,
  Completed = SharedWorkflowStateValue.Executed,
  Failed = "failed",
  Cancelled = "cancelled"
}

export enum IncidentTaskEscalationKind {
  None = "none",
  Approval = "approval",
  PhoneBridge = "phone_bridge",
  VendorContact = "vendor_contact",
  CodeBridge = "code_bridge",
  HumanResponse = "human_response"
}

export enum IncidentHandoffState {
  None = "none",
  Queued = "queued",
  Accepted = "accepted",
  Blocked = "blocked"
}

export enum IncidentTimelineKind {
  SignalReceived = "signal_received",
  VerificationQueued = "verification_queued",
  VerificationCompleted = "verification_completed",
  GitHubSynced = "github_synced",
  LlmPlanningRequested = "llm_planning_requested",
  LlmPlanningFailed = "llm_planning_failed",
  RecoveryProposed = "recovery_proposed",
  RecoveryActionClassified = "recovery_action_classified",
  RemediationSessionRequested = "remediation_session_requested",
  HumanDecisionQueued = "human_decision_queued",
  OwnerEscalationQueued = "owner_escalation_queued",
  ManualFallbackRequired = "manual_fallback_required",
  NotificationDecisionRecorded = "notification_decision_recorded",
  NotificationSent = "notification_sent",
  FailoverRequested = "failover_requested",
  StatusChanged = "status_changed",
  Resolved = "resolved"
}

export enum IncidentLearningRecordKind {
  MissingSignal = "missing_signal",
  MissingVerificationCheck = "missing_verification_check",
  RunbookImprovement = "runbook_improvement",
  ToolCapabilityProposal = "tool_capability_proposal",
  McpProposal = "mcp_proposal"
}

export enum IncidentCapabilityProposalState {
  Proposed = "proposed",
  NeedsReplay = "needs_replay",
  NeedsReview = "needs_review",
  Approved = SharedWorkflowStateValue.Approved,
  Rejected = "rejected",
  Activated = "activated"
}

export enum IncidentNotificationChannel {
  InApp = "in_app",
  MobilePush = "mobile_push",
  Email = "email",
  Sms = "sms",
  Voice = "voice",
  Webhook = "webhook",
  StatusPage = "status_page"
}

export enum IncidentNotificationTemplate {
  Investigating = "investigating",
  Identified = "identified",
  Monitoring = "monitoring",
  Resolved = "resolved",
  FollowUpPending = "follow_up_pending"
}

export enum RecoveryActionKind {
  DiagnosticCheck = "diagnostic_check",
  BoundedMitigation = "bounded_mitigation",
  ProviderAdminAction = "provider_admin_action",
  TelephonyReroute = "telephony_reroute",
  ServiceRestart = "service_restart",
  DeployRollback = "deploy_rollback",
  CustomerNotification = "customer_notification",
  GitHubSync = "github_sync",
  WorkerRemediationSession = "worker_remediation_session",
  ManualChecklist = "manual_checklist"
}

export enum IncidentApiActorRole {
  Operator = "operator",
  Admin = "admin"
}

export enum IncidentApiHeader {
  TenantId = "x-resilience-tenant-id",
  ActorId = "x-resilience-actor-id",
  ActorRole = "x-resilience-actor-role",
  ProviderCallbackSecret = "x-ops-bridge-provider-callback-secret"
}

export enum IncidentApiErrorCode {
  AuthenticationRequired = "incident_authentication_required",
  ActorForbidden = "incident_actor_forbidden",
  BodyTooLarge = "incident_body_too_large",
  InvalidJson = "incident_invalid_json",
  InvalidRequest = "incident_invalid_request",
  IncidentAuthorityUnavailable = "incident_authority_unavailable",
  IncidentNotFound = "incident_not_found",
  RouteNotFound = "incident_route_not_found",
  MethodNotAllowed = "incident_method_not_allowed"
}

export const incidentApiRoutes = {
  incidents: "/api/incidents",
  incidentCommunications: "/api/incidents/:incidentId/communications",
  incidentApprovals: "/api/incidents/:incidentId/approvals",
  incidentReopen: "/api/incidents/:incidentId/reopen",
  endpointCheckReports: "/api/incidents/intake/endpoint-checks",
  providerCallbackReports: "/api/incidents/intake/provider-callbacks",
  operatorChatReports: "/api/incidents/intake/operator-chat-reports",
  operatorVoiceReports: "/api/incidents/intake/operator-voice-reports"
} as const;

export interface CreateIncidentRequest {
  readonly rawSummary: string;
  readonly appId?: string | null;
  readonly source?: IncidentSignalSource;
  readonly serviceHint?: string | null;
  readonly environment?: string | null;
  readonly providerEventId?: string | null;
  readonly observedAt?: string;
  readonly customerLocale?: LocaleCode;
  readonly customerAudienceRef?: string | null;
  readonly ownerRef?: string | null;
  readonly severity?: IncidentSeverity;
}

export interface ResolveIncidentRequest {
  readonly rootCause: string;
}

export interface ReopenIncidentRequest {
  readonly reason: string;
}

export interface IncidentCommunicationTransitionRequest {
  readonly transition: IncidentCommunicationTransition;
  readonly customerSafeSummary?: string | null;
  readonly locale?: LocaleCode | null;
}

export interface IncidentApprovalTransitionRequest {
  readonly transition: IncidentApprovalTransition;
  readonly decisionId?: string | null;
  readonly assignedRole?: IncidentDecisionItem["assignedRole"] | null;
  readonly ownerRef?: string | null;
  readonly reason?: string | null;
}

export interface IncidentOption {
  readonly value: string;
  readonly label: string;
}

export interface IncidentCreateOptions {
  readonly apps: readonly IncidentOption[];
  readonly environmentsByApp: Readonly<Record<string, readonly IncidentOption[]>>;
  readonly servicesByApp: Readonly<Record<string, readonly IncidentOption[]>>;
  readonly sources: readonly IncidentOption[];
  readonly locales: readonly IncidentOption[];
  readonly audiences: readonly IncidentOption[];
  readonly owners: readonly IncidentOption[];
  readonly severities: readonly IncidentOption[];
}

export type IncidentQueueSort = "priority" | "customers" | "status" | "service";

export interface IncidentListFilters {
  readonly search?: string | null;
  readonly appId?: string | null;
  readonly environment?: string | null;
  readonly severity?: string | null;
  readonly status?: string | null;
  readonly service?: string | null;
  readonly sort?: IncidentQueueSort;
}

export interface IncidentQueueItem {
  readonly id: string;
  readonly appId: string;
  readonly serviceId: string;
  readonly title: string;
  readonly summary: string;
  readonly blocker: string;
  readonly ownerRef: string;
  readonly customerCount: number;
  readonly complaintCount: number;
  readonly environment: string;
  readonly severity: IncidentSeverity;
  readonly status: IncidentStatus;
  readonly createdAt: string;
  readonly updatedAt: string;
}

export interface IncidentListResponse {
  readonly ok: true;
  readonly incidents: readonly IncidentQueueItem[];
  readonly filters: IncidentListFilters;
  readonly total: number;
  readonly createOptions: IncidentCreateOptions;
}

export interface IncidentDetailResponse {
  readonly ok: true;
  readonly detail: IncidentDetail;
}

export interface IncidentApiErrorResponse {
  readonly ok: false;
  readonly code: IncidentApiErrorCode;
}

export type IncidentApiResponse = IncidentDetailResponse | IncidentApiErrorResponse;

export interface IncidentSignal {
  readonly id: string;
  readonly incidentId: string;
  readonly tenantId: string;
  readonly serviceId: string;
  readonly environment: string;
  readonly source: IncidentSignalSource;
  readonly confidence: IncidentSignalConfidence;
  readonly redactedSummary: string;
  readonly fingerprint: string;
  readonly providerEventId: string | null;
  readonly actorId: string | null;
  readonly observedAt: string;
  readonly receivedAt: string;
  readonly feedAuthority?: IncidentFeedAuthority;
}

export interface IncidentEvidence {
  readonly id: string;
  readonly incidentId: string;
  readonly kind: IncidentSignalSource | "log_excerpt" | "metric" | "runbook" | "llm_summary";
  readonly gateId?: string | null;
  readonly actionId?: string | null;
  readonly label: string;
  readonly redactedBody: string;
  readonly sourceReference: string | null;
  readonly customerSafe?: boolean;
  readonly createdAt: string;
}

export interface IncidentRunbookLink {
  readonly runbookId: string;
  readonly incidentType: IncidentRunbookType | null;
  readonly title: string;
  readonly toolIds: readonly string[];
  readonly manualFallbackAvailable: boolean;
}

export interface IncidentGitHubLink {
  readonly issueNumber: number | null;
  readonly issueUrl: string | null;
  readonly externalId: string | null;
  readonly lastSyncedAt: string | null;
}

export interface IncidentNotificationAttempt {
  readonly id: string;
  readonly incidentId: string;
  readonly channel: IncidentNotificationChannel;
  readonly template: IncidentNotificationTemplate;
  readonly locale: LocaleCode;
  readonly audienceRef: string;
  readonly state: IncidentNotificationState;
  readonly approvalState: ApprovalState;
  readonly idempotencyKey: string;
  readonly providerMessageId: string | null;
  readonly createdAt: string;
  readonly sentAt: string | null;
}

export interface IncidentCustomerImpactRow {
  readonly customerRef: string;
  readonly displayName: string;
  readonly tier: "enterprise" | "growth" | "starter" | "internal";
  readonly affectedServiceRef: string;
  readonly affectedRouteRef: string;
  readonly state: "impacted" | "monitoring" | "recovered";
  readonly ownerRef: string;
  readonly complaintCount: number;
  readonly resolutionNotificationState: IncidentNotificationState;
}

export interface IncidentComplaintRow {
  readonly complaintRef: string;
  readonly customerRef: string | null;
  readonly displayName: string;
  readonly source: IncidentSignalSource | IncidentNotificationChannel;
  readonly newestAt: string;
  readonly complaintCount: number;
  readonly linked: boolean;
}

export interface IncidentResolutionNotificationReceipt {
  readonly receiptRef: string;
  readonly customerRef: string;
  readonly displayName: string;
  readonly channel: IncidentNotificationChannel;
  readonly state: IncidentNotificationState;
  readonly sentAt: string | null;
  readonly deliveredAt: string | null;
}

export interface IncidentCommunicationSummary {
  readonly impactedCustomerCount: number;
  readonly enterpriseCustomerCount: number;
  readonly complaintCount: number;
  readonly outageNoticeState: "draft" | "posted" | "not_required";
  readonly customerNotificationState: IncidentNotificationState;
  readonly resolutionNotificationState: IncidentNotificationState;
  readonly ownerAcknowledged: boolean;
  readonly deliveryRiskCount: number;
  readonly statusPagePosted: boolean;
}

export interface IncidentAccountability {
  readonly incidentCommanderRef: string;
  readonly serviceOwnerRef: string;
  readonly communicationsOwnerRef: string;
  readonly currentAssigneeRef: string;
  readonly handoffState: IncidentHandoffState;
  readonly handoffTargetRef: string | null;
  readonly ownerAcknowledgedAt: string | null;
}

export interface IncidentLifecycle {
  readonly detectedAt: string;
  readonly acknowledgedAt: string | null;
  readonly identifiedAt: string | null;
  readonly mitigatedAt: string | null;
  readonly monitoringAt: string | null;
  readonly resolvedAt: string | null;
  readonly closedAt: string | null;
  readonly lastTransitionAt: string;
}

export interface IncidentRecoveryAction {
  readonly id: string;
  readonly incidentId: string;
  readonly kind: RecoveryActionKind;
  readonly actionRef: string;
  readonly toolId: string | null;
  readonly summary: string;
  readonly policyClass: PolicyClass;
  readonly requiresApproval: boolean;
  readonly approvalState: ApprovalState;
  readonly operationHash: string;
  readonly status: "proposed" | SharedWorkflowStateValue.Approved | SharedWorkflowStateValue.Executed | SharedWorkflowStateValue.Blocked | "failed";
}

export interface IncidentRecoveryProposal {
  readonly id: string;
  readonly incidentId: string;
  readonly advisor: "llm" | "deterministic" | "manual";
  readonly summary: string;
  readonly rootCauseHypothesis: string | null;
  readonly runbookId: string | null;
  readonly actions: readonly IncidentRecoveryAction[];
  readonly redactionApplied: boolean;
  readonly createdAt: string;
}

export interface IncidentDecisionItem {
  readonly id: string;
  readonly incidentId: string;
  readonly kind: IncidentDecisionKind;
  readonly state: IncidentDecisionState;
  readonly title: string;
  readonly summary: string;
  readonly policyClass: PolicyClass;
  readonly approvalState: ApprovalState;
  readonly assignedRole: "operator" | "admin" | "comms_approver" | "owner";
  readonly relatedResourceId: string | null;
  readonly dueAt: string | null;
  readonly createdAt: string;
  readonly resolvedAt: string | null;
}

export interface IncidentOwnerEscalation {
  readonly id: string;
  readonly incidentId: string;
  readonly channel: IncidentNotificationChannel;
  readonly recipientRef: string;
  readonly state: IncidentOwnerEscalationState;
  readonly escalationLevel: number;
  readonly reason: string;
  readonly dueAt: string;
  readonly createdAt: string;
  readonly acknowledgedAt: string | null;
}

export interface IncidentResponderAssignment {
  readonly id: string;
  readonly incidentId: string;
  readonly responderRef: string;
  readonly displayName: string;
  readonly kind: IncidentResponderKind;
  readonly role: IncidentResponderRole;
  readonly authorityClass: PolicyClass;
  readonly assignedTaskId: string | null;
  readonly assignedGateId: string | null;
  readonly acknowledgementState: IncidentResponderAcknowledgementState;
  readonly blocked: boolean;
  readonly currentAction: string;
  readonly escalationPolicy: IncidentTaskEscalationKind;
  readonly lastActivityAt: string;
  readonly auditRefs: readonly string[];
  readonly evidenceRefs: readonly string[];
}

export interface IncidentTask {
  readonly id: string;
  readonly incidentId: string;
  readonly gateId: string;
  readonly title: string;
  readonly summary: string;
  readonly status: IncidentTaskStatus;
  readonly authorityClass: PolicyClass;
  readonly responderRole: IncidentResponderRole;
  readonly ownerResponderRef: string;
  readonly primaryCta: string;
  readonly primaryCtaKind: IncidentTaskEscalationKind;
  readonly requiresApproval: boolean;
  readonly evidencePrompt: string;
  readonly evidenceRefs: readonly string[];
  readonly createdAt: string;
  readonly updatedAt: string;
}

export interface IncidentFailoverAction {
  readonly id: string;
  readonly incidentId: string;
  readonly target: string;
  readonly policyClass: PolicyClass;
  readonly requiresApproval: boolean;
  readonly rollbackActionId: string | null;
  readonly status: "requested" | SharedWorkflowStateValue.Approved | "active" | "rolled_back" | SharedWorkflowStateValue.Blocked;
}

export interface IncidentLearningRecord {
  readonly id: string;
  readonly incidentId: string;
  readonly kind: IncidentLearningRecordKind;
  readonly title: string;
  readonly summary: string;
  readonly evidenceRefs: readonly string[];
  readonly createdByRef: string;
  readonly createdAt: string;
}

export interface IncidentCapabilityProposal {
  readonly id: string;
  readonly incidentId: string;
  readonly kind: IncidentLearningRecordKind.ToolCapabilityProposal | IncidentLearningRecordKind.McpProposal;
  readonly title: string;
  readonly summary: string;
  readonly proposedActionRef: string;
  readonly state: IncidentCapabilityProposalState;
  readonly replayRequired: boolean;
  readonly reviewRequired: boolean;
  readonly evidenceRefs: readonly string[];
  readonly createdByRef: string;
  readonly createdAt: string;
}

export interface IncidentTimelineEvent {
  readonly id: string;
  readonly incidentId: string;
  readonly kind: IncidentTimelineKind;
  readonly summary: string;
  readonly redactedBody: string | null;
  readonly actorId: string | null;
  readonly auditCorrelationId: string;
  readonly createdAt: string;
}

export interface Incident {
  readonly id: string;
  readonly tenantId: string;
  readonly appId?: string;
  readonly serviceId: string;
  readonly incidentType: IncidentRunbookType | null;
  readonly environment: string;
  readonly fingerprint: string;
  readonly severity: IncidentSeverity;
  readonly status: IncidentStatus;
  readonly confidence: IncidentSignalConfidence;
  readonly affectedAudience: IncidentAffectedAudience;
  readonly notificationState: IncidentNotificationState;
  readonly runbook: IncidentRunbookLink | null;
  readonly accountability: IncidentAccountability;
  readonly lifecycle: IncidentLifecycle;
  readonly github: IncidentGitHubLink;
  readonly manualFallbackRequired: boolean;
  readonly feed?: IncidentFeedMetadata;
  readonly createdAt: string;
  readonly updatedAt: string;
  readonly resolvedAt: string | null;
}

export interface IncidentDetail {
  readonly incident: Incident;
  readonly communicationSummary: IncidentCommunicationSummary;
  readonly customerImpact: readonly IncidentCustomerImpactRow[];
  readonly complainingCustomers: readonly IncidentComplaintRow[];
  readonly resolutionNotifications: readonly IncidentResolutionNotificationReceipt[];
  readonly signals: readonly IncidentSignal[];
  readonly evidence: readonly IncidentEvidence[];
  readonly timeline: readonly IncidentTimelineEvent[];
  readonly recoveryProposals: readonly IncidentRecoveryProposal[];
  readonly notifications: readonly IncidentNotificationAttempt[];
  readonly decisions: readonly IncidentDecisionItem[];
  readonly ownerEscalations: readonly IncidentOwnerEscalation[];
  readonly responders?: readonly IncidentResponderAssignment[];
  readonly tasks?: readonly IncidentTask[];
  readonly activeTask?: IncidentTask | null;
  readonly toolCatalog?: readonly PublicToolCatalogEntry[];
  readonly failovers: readonly IncidentFailoverAction[];
  readonly learningRecords: readonly IncidentLearningRecord[];
  readonly capabilityProposals: readonly IncidentCapabilityProposal[];
}

export interface IncidentResolutionSummary {
  readonly incidentId: string;
  readonly status: IncidentStatus.Resolved | IncidentStatus.Closed;
  readonly resolvedBy: string;
  readonly rootCause: string;
  readonly followUpRequired: boolean;
  readonly resolvedAt: string;
}

export const incidentStatuses = Object.values(IncidentStatus);
export const incidentSeverities = Object.values(IncidentSeverity);
export const incidentRunbookTypes = Object.values(IncidentRunbookType);
export const incidentSignalSources = Object.values(IncidentSignalSource);
export const incidentSignalConfidences = Object.values(IncidentSignalConfidence);
export const incidentCommunicationTransitions = Object.values(IncidentCommunicationTransition);
export const incidentApprovalTransitions = Object.values(IncidentApprovalTransition);
export const incidentDecisionKinds = Object.values(IncidentDecisionKind);
export const incidentDecisionStates = Object.values(IncidentDecisionState);
export const incidentOwnerEscalationStates = Object.values(IncidentOwnerEscalationState);
export const incidentResponderKinds = Object.values(IncidentResponderKind);
export const incidentResponderRoles = Object.values(IncidentResponderRole);
export const incidentResponderAcknowledgementStates = Object.values(IncidentResponderAcknowledgementState);
export const incidentTaskStatuses = Object.values(IncidentTaskStatus);
export const incidentTaskEscalationKinds = Object.values(IncidentTaskEscalationKind);
export const incidentHandoffStates = Object.values(IncidentHandoffState);
export const incidentLearningRecordKinds = Object.values(IncidentLearningRecordKind);
export const incidentCapabilityProposalStates = Object.values(IncidentCapabilityProposalState);
export const incidentApiActorRoles = Object.values(IncidentApiActorRole);

export function isIncidentStatus(value: string | null | undefined): value is IncidentStatus {
  return value !== null && value !== undefined && incidentStatuses.some((status) => status === value);
}

export function isIncidentSeverity(value: string | null | undefined): value is IncidentSeverity {
  return value !== null && value !== undefined && incidentSeverities.some((severity) => severity === value);
}

export function isIncidentRunbookType(value: string | null | undefined): value is IncidentRunbookType {
  return value !== null && value !== undefined && incidentRunbookTypes.some((incidentType) => incidentType === value);
}

export function isIncidentSignalSource(value: string | null | undefined): value is IncidentSignalSource {
  return value !== null && value !== undefined && incidentSignalSources.some((source) => source === value);
}

export function isIncidentSignalConfidence(value: string | null | undefined): value is IncidentSignalConfidence {
  return value !== null && value !== undefined && incidentSignalConfidences.some((confidence) => confidence === value);
}

export function isIncidentCommunicationTransition(value: string | null | undefined): value is IncidentCommunicationTransition {
  return value !== null && value !== undefined && incidentCommunicationTransitions.some((transition) => transition === value);
}

export function isIncidentApprovalTransition(value: string | null | undefined): value is IncidentApprovalTransition {
  return value !== null && value !== undefined && incidentApprovalTransitions.some((transition) => transition === value);
}

export function isIncidentDecisionKind(value: string | null | undefined): value is IncidentDecisionKind {
  return value !== null && value !== undefined && incidentDecisionKinds.some((kind) => kind === value);
}

export function isIncidentDecisionState(value: string | null | undefined): value is IncidentDecisionState {
  return value !== null && value !== undefined && incidentDecisionStates.some((state) => state === value);
}

export function isIncidentOwnerEscalationState(value: string | null | undefined): value is IncidentOwnerEscalationState {
  return value !== null && value !== undefined && incidentOwnerEscalationStates.some((state) => state === value);
}

export function isIncidentResponderKind(value: string | null | undefined): value is IncidentResponderKind {
  return value !== null && value !== undefined && incidentResponderKinds.some((kind) => kind === value);
}

export function isIncidentResponderRole(value: string | null | undefined): value is IncidentResponderRole {
  return value !== null && value !== undefined && incidentResponderRoles.some((role) => role === value);
}

export function isIncidentResponderAcknowledgementState(value: string | null | undefined): value is IncidentResponderAcknowledgementState {
  return value !== null && value !== undefined && incidentResponderAcknowledgementStates.some((state) => state === value);
}

export function isIncidentTaskStatus(value: string | null | undefined): value is IncidentTaskStatus {
  return value !== null && value !== undefined && incidentTaskStatuses.some((status) => status === value);
}

export function isIncidentTaskEscalationKind(value: string | null | undefined): value is IncidentTaskEscalationKind {
  return value !== null && value !== undefined && incidentTaskEscalationKinds.some((kind) => kind === value);
}

export function isIncidentHandoffState(value: string | null | undefined): value is IncidentHandoffState {
  return value !== null && value !== undefined && incidentHandoffStates.some((state) => state === value);
}

export function isIncidentLearningRecordKind(value: string | null | undefined): value is IncidentLearningRecordKind {
  return value !== null && value !== undefined && incidentLearningRecordKinds.some((kind) => kind === value);
}

export function isIncidentCapabilityProposalState(value: string | null | undefined): value is IncidentCapabilityProposalState {
  return value !== null && value !== undefined && incidentCapabilityProposalStates.some((state) => state === value);
}

export function isIncidentApiActorRole(value: string | null | undefined): value is IncidentApiActorRole {
  return value !== null && value !== undefined && incidentApiActorRoles.some((role) => role === value);
}
